import HierarchyItemsTableContext from '@components/UI-Components/HierarchyWiseItemsTable/contexts/hierarchy-items-table-context'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { ListTableView } from '@modules/order/stores/view/List/ListViewTableService'
import { getEnumValue } from '@utils/get-enum-value'
import some from 'lodash/some'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useEffect, useMemo, useRef } from 'react'
import QuantitySwitch from '@modules/order/components/SAOrderTopbar/components/QuantitySwitch/QuantitySwitch'
import { QuantityType } from '@components/UI-Components/QuantityType/constants/QuantityType'
import filter from 'lodash/filter'
import QuantityChangeMessage from '@modules/order/components/SAOrderTopbar/components/QuantityChangeMessage/QuantityChangeMessage'
import { View } from '@modules/order/constants/views'
import { stores } from '@stores'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { getCurrentGroupMapping } from '@modules/order/utils/dynamic-group-mapping'
import { useTranslation } from 'react-i18next'
import EnhancedOrderTableView from '@modules/order/components/OrderTableView/EnhancedOrderTableView'
import { HIERARCHY_ROOT_ID } from '@modules/order/constants/uiConstants'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import {
  getVisibleGlobalKpiRows,
  getVisibleKpiRows
} from '@modules/order/constants/QuantityHeaderKPI'
import { getChannelName } from '@utils/get-channel-name'
import map from 'lodash/map'
import { TABLE_SETTINGS_VIEWS } from '@modules/showroom/EnhancedTableSettings/store/EnhancedTableSettingsStore'
import { isEqual } from 'lodash'

export interface EnhancedListViewContainerProps {
  tableStoreInstance: any
  tableContextValue: any
}

const EnhancedListViewContainer: FC<EnhancedListViewContainerProps> = ({
  tableStoreInstance,
  tableContextValue
}) =>
  useObserver(function useHook() {
    const {
      uiStore: {
        dataStore: { summary, summaryLoading, tableSetting },
        quantityTypeStore: { type }
      }
    } = useOrderContext()
    const prevDoorOrders = useRef(summary?.doorOrders)
    const viewContext = useViewContext()
    const { t } = useTranslation()
    const uiStore = viewContext.uiStore as EnhancedListUIStore<any>
    const {
      dataStore: {
        heirarchy,
        isQuantityUpdated,
        hierarchyInRange,
        enhancedWarningPopupStore: {
          fetchOrderWarningsForDropdown,
          warningFiltersLoading,
          getValidationWarningsAttributes
        }
      },
      isValueSelected,
      setIsValueSelected,
      refresh,
      applySortOptionsAgain,
      setCurrentViewConfig,
      prepareViewData
    } = uiStore

    const {
      nav: {
        queryParams: { orderId, clientType, view: mainView, subSection }
      },
      productConfiguration: {
        loadedConfigData,
        defaultValuesFromConfig,
        getGlobalTableSettingsByView,
        isWarningModeActive,
        findSpecificAttribute
      }
    } = stores

    useEffect(() => {
      if (loadedConfigData) {
        if (!isWarningModeActive) {
          setCurrentViewConfig?.()
          prepareViewData?.()
        }
        if (isWarningModeActive && !warningFiltersLoading) {
          setCurrentViewConfig?.()
          prepareViewData?.()
        }
      }
    }, [loadedConfigData, setCurrentViewConfig, prepareViewData, subSection, orderId, warningFiltersLoading, isWarningModeActive])

    const isRetailView = clientType === getEnumValue(VIPClientType.Internal)

    const isQuantityView = isRetailView
      ? subSection === ListTableView.Quantity
      : mainView === View.List
    useEffect(() => {
      if (isWarningModeActive) {
        fetchOrderWarningsForDropdown(true)
        getValidationWarningsAttributes()
      }
    }, [isWarningModeActive, fetchOrderWarningsForDropdown, getValidationWarningsAttributes])

    const globalSettings = useMemo(() => {
      return getGlobalTableSettingsByView(
        'orderCollection',
        isQuantityView ? TABLE_SETTINGS_VIEWS.Quantity : TABLE_SETTINGS_VIEWS.Summary,
        getChannelName(clientType)
      )
    }, [getGlobalTableSettingsByView, isQuantityView, clientType])

    const hiddenColAttributeIds = map(
      filter(globalSettings?.productsTable?.columns, column => !column?.showHide) ?? [],
      field => field.attributeId
    )
    const hiddenRowAttributeIds = map(
      filter(globalSettings?.doorKpiTable?.rows, row => !row?.showHide) ?? [],
      field => field.attributeId
    )

    const tableSettingRows = useMemo(() => {
      return isQuantityView ? tableSetting?.Quantity?.rows : {}
    }, [isQuantityView, tableSetting?.Quantity?.rows])

    let mapping = Object.keys(
      getCurrentGroupMapping(
        hierarchyInRange(0, 1, heirarchy, 0),
        defaultValuesFromConfig?.empty,
        findSpecificAttribute
      )
    )
    const productInfoColumnName = useMemo(() => {
      return mapping.reduce((acc, attributeId) => {
        const attributeObj = findSpecificAttribute(attributeId)
        if (attributeObj) {
          acc = acc
            ? acc.concat('/ ', `${t(attributeObj?.labelKey, attributeObj?.displayName)}`)
            : `${t(attributeObj?.labelKey, attributeObj?.displayName)}`
        }
        return acc
      }, '')
    }, [mapping, findSpecificAttribute, t])

    const columns = useMemo(
      () =>
        summaryLoading
          ? []
          : uiStore.tableService
              .getColumns(
                isQuantityView
                  ? summary.doorOrder
                    ? [
                        {
                          ...summary.doorOrder,
                          door: summary.door,
                          id: summary.doorOrder.doorOrderId
                        }
                      ]
                    : filter(summary.doorOrders, doorOrder => !doorOrder?.deleted)
                  : [],
                isRetailView,
                summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize,
                isValueSelected,
                type,
                productInfoColumnName,
                hiddenRowAttributeIds,
                tableSettingRows
              )
              .filter(field => !hiddenColAttributeIds?.includes(field?.attributeId)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        summary?.doorOrders,
        summary?.doorOrder,
        subSection,
        heirarchy,
        type,
        isValueSelected,
        summaryLoading,
        isQuantityView,
        hiddenRowAttributeIds,
        tableSettingRows
      ]
    )
    const visibleKpiRows = useMemo(() => {
      return some(columns, column => column?.columns?.length) || isRetailView
        ? getVisibleKpiRows(
            type,
            VIPChannel.Retail,
            hiddenRowAttributeIds,
            tableSettingRows
          )
        : getVisibleKpiRows(
            type,
            VIPChannel.Wholesale,
            hiddenRowAttributeIds,
            tableSettingRows
          )
    }, [isRetailView, columns, hiddenRowAttributeIds, type, tableSettingRows])

    const visibleGlobalKpiRows = useMemo(() => {
      return some(columns, column => column?.columns?.length) || isRetailView
        ? getVisibleGlobalKpiRows(type, VIPChannel.Retail, hiddenRowAttributeIds)
        : getVisibleGlobalKpiRows(type, VIPChannel.Wholesale, hiddenRowAttributeIds)
    }, [isRetailView, columns, hiddenRowAttributeIds, type])

    const rootHeight = useMemo(() => {
      if (isQuantityView) {
        return ((visibleKpiRows?.length || 1) + 1) * 30
      } else {
        return 30
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isQuantityView, visibleKpiRows, hiddenRowAttributeIds])

    useEffect(() => {
      if (
        prevDoorOrders.current &&
        !isEqual(prevDoorOrders.current, summary?.doorOrders)
      ) {
        refresh()
      }
      prevDoorOrders.current = summary?.doorOrders
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [summary?.doorOrders])

    const sortAgainAction = useMemo(
      () => [
        {
          name: 'Sort Again Message',
          showIcon: isRetailView && mainView === View.List && isQuantityUpdated,
          isCustomElement: true,
          customElement: QuantityChangeMessage,
          customElementProps: {
            isQuantityUpdated: isQuantityUpdated,
            sortAgain: applySortOptionsAgain
          }
        }
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isQuantityUpdated]
    )
    return (
      <HierarchyItemsTableContext.Provider value={{ store: uiStore }}>
        <EnhancedOrderTableView
          columns={columns}
          headerRows={visibleGlobalKpiRows}
          rootHeight={rootHeight}
          rootId={HIERARCHY_ROOT_ID}
          selectedView={isQuantityView ? ListTableView.Quantity : ListTableView.Summary}
          rightIcons={[
            ...sortAgainAction,
            {
              name: 'switch qty',
              showIcon: isQuantityView,
              isCustomElement: true,
              customElement: QuantitySwitch,
              customElementProps: {
                checked: isValueSelected,
                onChange: setIsValueSelected,
                disabled: QuantityType.Product === type
              }
            }
          ]}
          tableStoreInstance={tableStoreInstance}
          tableContextValue={tableContextValue}
        />
      </HierarchyItemsTableContext.Provider>
    )
  })

export default EnhancedListViewContainer
